/**
 * Die Ecken eines Raumpolygons verschieben, einfügen und löschen.
 *
 * Reine Rechnung auf Punktlisten, getrennt von paper.js und vom Editor: Was hier steht, entscheidet
 * über die Form eines Raums, und das ist die Stelle, an der ein Fehler in die Daten geht. Die
 * Anbindung an Maus und Leinwand ist austauschbar, diese Regeln sind es nicht.
 *
 * Ein Polygon ist eine offene Liste — der letzte Punkt schließt **nicht** auf den ersten. Die
 * Kante von der letzten zur ersten Ecke ist trotzdem eine Kante und trägt hier den letzten Index;
 * ohne sie ließe sich genau an der Stelle keine Ecke einfügen, an der ein Raum sich schließt.
 */

export interface Point {
    x: number;
    y: number;
}

/**
 * Weniger als drei Ecken sind kein Raum, sondern eine Strecke.
 *
 * Die Grenze steht hier und nicht in der Oberfläche, damit sie nicht davon abhängt, über welchen
 * Weg gelöscht wird.
 */
export const MIN_VERTICES = 3;

/** Ob sich überhaupt noch eine Ecke entfernen lässt. */
export const canRemoveVertex = (shape: Point[] | null | undefined): boolean =>
    Array.isArray(shape) && shape.length > MIN_VERTICES;

/** Eine Ecke an eine neue Stelle setzen. Ein Index daneben lässt die Form unverändert. */
export const moveVertex = (shape: Point[], index: number, to: Point): Point[] => {
    if (!Array.isArray(shape) || index < 0 || index >= shape.length) return shape ?? [];

    return shape.map((point, at) => (at === index ? { x: to.x, y: to.y } : point));
};

/**
 * Eine Ecke entfernen — oder die Form unverändert lassen, wo das die dritte wäre.
 *
 * Gibt bewusst die alte Liste zurück statt zu werfen: Der Aufrufer sitzt an einem Doppelklick, und
 * eine Ausnahme wäre dort nichts, was sich sinnvoll behandeln ließe. Wer wissen will, ob es geht,
 * fragt vorher `canRemoveVertex`.
 */
export const removeVertex = (shape: Point[], index: number): Point[] => {
    if (!canRemoveVertex(shape) || index < 0 || index >= shape.length) return shape ?? [];

    return shape.filter((_point, at) => at !== index);
};

/**
 * Eine Ecke in die Kante nach `edgeIndex` einfügen.
 *
 * Der neue Punkt landet **zwischen** den beiden Ecken der Kante, nicht am Ende der Liste — sonst
 * wäre die Reihenfolge der Ecken eine andere und das Polygon schlüge einen Haken quer durch den
 * Raum.
 */
export const insertVertexAfter = (shape: Point[], edgeIndex: number, at: Point): Point[] => {
    if (!Array.isArray(shape) || shape.length === 0) return shape ?? [];
    if (edgeIndex < 0 || edgeIndex >= shape.length) return shape;

    const next = [...shape];

    next.splice(edgeIndex + 1, 0, { x: at.x, y: at.y });

    return next;
};

/**
 * Der Punkt auf der Strecke `from`–`to`, der `point` am nächsten liegt.
 *
 * Beschränkt auf die Strecke, nicht auf die Gerade durch sie: Ohne die Begrenzung läge die neue
 * Ecke bei einem Klick neben dem Ende der Kante außerhalb des Raums.
 */
export const projectOnSegment = (point: Point, from: Point, to: Point): Point => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const laenge = dx * dx + dy * dy;

    // Zwei Ecken auf demselben Fleck haben keine Richtung; dann ist der Fußpunkt die Ecke selbst.
    if (laenge === 0) return { x: from.x, y: from.y };

    const t = Math.min(1, Math.max(0, ((point.x - from.x) * dx + (point.y - from.y) * dy) / laenge));

    return { x: from.x + t * dx, y: from.y + t * dy };
};

export interface EdgeHit {
    /** Die Kante beginnt bei dieser Ecke; die letzte Kante führt von der letzten zur ersten. */
    edgeIndex: number;
    /** Der Fußpunkt auf der Kante — dort gehört die neue Ecke hin. */
    at: Point;
    distance: number;
}

/**
 * Welche Kante einem Punkt am nächsten liegt, und wo auf ihr.
 *
 * `null` für alles, was kein Polygon ist. Bei gleichem Abstand gewinnt die frühere Kante — eine
 * Regel, die nichts bedeutet, außer dass dasselbe Klicken immer dasselbe ergibt.
 */
export const nearestEdge = (shape: Point[], point: Point): EdgeHit | null => {
    if (!Array.isArray(shape) || shape.length < 2) return null;

    // Wie `projectOnSegment`, aber ohne je Kante einen Punkt anzulegen: Der Aufruf sitzt an der
    // Mausbewegung, und angelegt wird nur der Fußpunkt der Siegerkante.
    let bestIndex = -1;
    let bestDistance = 0;
    let bestX = 0;
    let bestY = 0;

    for (let index = 0; index < shape.length; index += 1) {
        const from = shape[index];
        const to = shape[(index + 1) % shape.length];
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const laenge = dx * dx + dy * dy;
        let atX = from.x;
        let atY = from.y;

        if (laenge !== 0) {
            const t = Math.min(1, Math.max(0, ((point.x - from.x) * dx + (point.y - from.y) * dy) / laenge));

            atX = from.x + t * dx;
            atY = from.y + t * dy;
        }

        const distance = Math.hypot(point.x - atX, point.y - atY);

        if (bestIndex < 0 || distance < bestDistance) {
            bestIndex = index;
            bestDistance = distance;
            bestX = atX;
            bestY = atY;
        }
    }

    return { edgeIndex: bestIndex, at: { x: bestX, y: bestY }, distance: bestDistance };
};

/** Welche Ecke einem Punkt am nächsten liegt — für den Griff unter dem Zeiger. */
export const nearestVertex = (
    shape: Point[],
    point: Point,
): { index: number; distance: number } | null => {
    if (!Array.isArray(shape) || shape.length === 0) return null;

    let best: { index: number; distance: number } | null = null;

    shape.forEach((vertex, index) => {
        const distance = Math.hypot(point.x - vertex.x, point.y - vertex.y);

        if (!best || distance < best.distance) {
            best = { index, distance };
        }
    });

    return best;
};

/**
 * Die Punktliste eines Raums, wie sie aus der Ablage kommt.
 *
 * `shape` ist ein JSON-Feld: Manche Datensätze kommen als fertige Liste zurück, andere als
 * Zeichenkette, und aus alten Importen auch als etwas, das keines von beidem ist. Ein Fehler hier
 * darf nicht die ganze Zeichenfläche mitnehmen — deshalb im Zweifel eine leere Liste, die den Raum
 * überspringt, statt einer Ausnahme.
 */
export const toPointList = (value: unknown): Point[] => {
    const raw = typeof value === 'string'
        ? (() => {
            try {
                return JSON.parse(value);
            } catch {
                return null;
            }
        })()
        : value;

    if (!Array.isArray(raw)) return [];

    return raw.filter(
        (point: any) =>
            point && typeof point.x === 'number' && typeof point.y === 'number'
            && Number.isFinite(point.x) && Number.isFinite(point.y),
    );
};

/**
 * Wie weit eine Pfeiltaste eine Ecke schiebt — in Millimetern.
 *
 * Millimeter, weil die abgelegte Form in Millimetern rechnet und die Fläche daraus in
 * Quadratmetern folgt. Eine Ecke „um einen Zentimeter" zu schieben ist eine Aussage über das
 * Gebäude; „um ein Pixel" wäre eine über den Bildschirm und hinge am Zoom.
 *
 * Drei Stufen, weil eine nicht reicht: Der Normalfall rückt zurecht, die grobe Stufe überbrückt
 * Strecken, und die feine trifft eine Kante, die auf den Millimeter stimmen soll.
 */
export const NUDGE_MM = { fine: 1, normal: 10, coarse: 100 };

export interface NudgeKeys {
    shiftKey?: boolean;
    altKey?: boolean;
}

/**
 * Der Versatz zu einer Pfeiltaste — oder `null` für jede andere Taste.
 *
 * `null` und nicht `{0,0}`: Der Aufrufer soll die Taste nur dann abfangen, wenn sie ihn angeht.
 * Ein Nullversatz sähe aus wie eine Bewegung und verschluckte alles andere.
 */
export const nudgeFor = (key: string, modifiers: NudgeKeys = {}): Point | null => {
    const step = modifiers.altKey
        ? NUDGE_MM.fine
        : modifiers.shiftKey
            ? NUDGE_MM.coarse
            : NUDGE_MM.normal;

    switch (key) {
        case 'ArrowLeft':
            return { x: -step, y: 0 };
        case 'ArrowRight':
            return { x: step, y: 0 };
        // Nach oben ist im Gebäudebild das kleinere y — dieselbe Richtung, die der Zeiger nimmt.
        case 'ArrowUp':
            return { x: 0, y: -step };
        case 'ArrowDown':
            return { x: 0, y: step };
        default:
            return null;
    }
};

/** Der Winkel an `vertex` zwischen seinen Nachbarn, in Grad — zum Prüfen und zum Anzeigen. */
export const angleAt = (prev: Point, vertex: Point, next: Point): number => {
    const a = { x: prev.x - vertex.x, y: prev.y - vertex.y };
    const b = { x: next.x - vertex.x, y: next.y - vertex.y };
    const lengths = Math.hypot(a.x, a.y) * Math.hypot(b.x, b.y);

    if (lengths === 0) return Number.NaN;

    const cosine = Math.min(1, Math.max(-1, (a.x * b.x + a.y * b.y) / lengths));

    return (Math.acos(cosine) * 180) / Math.PI;
};

/**
 * Die Ecke auf einen rechten Winkel einschnappen.
 *
 * Der Winkel an einer Ecke hängt nur an ihren beiden Nachbarn — er ist lokal. Man muss also keine
 * Hauptrichtung des Grundrisses schätzen, um ihn auf 90° zu bringen; eine geschätzte Orientierung
 * beantwortet eine andere Frage (Wände parallel zur Gebäudeachse halten) und wäre hier nur eine
 * Annahme, die schiefgehen kann.
 *
 * Die Menge aller Punkte, von denen aus die Strecke `prev`–`next` unter 90° erscheint, ist der
 * **Thaleskreis** über dieser Strecke. Einschnappen heißt deshalb: den Zeiger auf diesen Kreis
 * setzen. Das Ergebnis ist exakt rechtwinklig, nicht ungefähr.
 *
 * **Gefangen wird über den Winkel, nicht über den Abstand zum Kreis.** Eine Toleranz in Pixeln
 * ergäbe ein schmales Band um eine Kurve: Beim Ziehen kreuzt man es kurz, das Zeichen blitzt auf
 * und ist wieder fort — von „einschnappen" wäre nichts zu spüren. Ein Toleranzwinkel ergibt
 * dagegen einen Keil, der mit dem Raum mitwächst und sich anfasst wie ein Magnet: In seiner Nähe
 * hält die Ecke am rechten Winkel fest, und wer loslässt, während das Zeichen steht, bekommt
 * genau 90°.
 *
 * `null`, wo nicht eingeschnappt wird: außerhalb des Toleranzwinkels, oder wo die beiden Nachbarn
 * zusammenfallen und es keinen Kreis gibt.
 */
export const snapToRightAngle = (
    cursor: Point,
    prev: Point,
    next: Point,
    toleranceDegrees: number,
): Point | null => {
    const centre = { x: (prev.x + next.x) / 2, y: (prev.y + next.y) / 2 };
    const radius = Math.hypot(prev.x - next.x, prev.y - next.y) / 2;

    if (radius === 0) return null;

    const dx = cursor.x - centre.x;
    const dy = cursor.y - centre.y;
    const reach = Math.hypot(dx, dy);

    // Genau im Mittelpunkt gibt es keine Richtung, in die zu projizieren wäre.
    if (reach === 0) return null;

    // Wie weit der Winkel an dieser Stelle von 90° entfernt ist — das ist das Maß, das zählt.
    if (Math.abs(angleAt(prev, cursor, next) - 90) > toleranceDegrees) return null;

    return {
        x: centre.x + (dx / reach) * radius,
        y: centre.y + (dy / reach) * radius,
    };
};

/**
 * Das Winkelzeichen an einer Ecke: **Bogen mit Punkt**.
 *
 * So wird ein rechter Winkel im deutschen technischen Zeichnen dargestellt — ein Bogen zwischen den
 * beiden Schenkeln, und darin ein Punkt. Der Bogen allein bedeutet nur „hier ist ein Winkel"; erst
 * der Punkt sagt, dass es ein rechter ist. Das kleine Quadrat ist die angelsächsische Schreibweise
 * und in einer Bauzeichnung hierzulande falsch.
 *
 * Zurückgegeben werden die drei Punkte, aus denen sich der Bogen ziehen lässt — Anfang, ein Punkt
 * darauf, Ende —, dazu die Lage des Punktes im Inneren.
 *
 * `radius` ist der Bogenradius in denselben Einheiten wie die Punkte; der Aufrufer rechnet ihn aus
 * Bildschirmpixeln um, damit das Zeichen bei jedem Zoom gleich groß bleibt.
 */
export interface AngleMarker {
    /** Auf dem ersten Schenkel. */
    from: Point;
    /** Auf der Winkelhalbierenden, auf dem Bogen — der dritte Punkt, den ein Bogen braucht. */
    through: Point;
    /** Auf dem zweiten Schenkel. */
    to: Point;
    /** Der Punkt im Inneren, der den Bogen zum rechten Winkel macht. */
    dot: Point;
}

export const rightAngleMarker = (
    prev: Point,
    vertex: Point,
    next: Point,
    radius: number,
): AngleMarker | null => {
    const unit = (from: Point) => {
        const dx = from.x - vertex.x;
        const dy = from.y - vertex.y;
        const length = Math.hypot(dx, dy);

        return length === 0 ? null : { x: dx / length, y: dy / length };
    };

    const a = unit(prev);
    const b = unit(next);

    // Ohne Richtung kein Zeichen: Eine Ecke, die auf ihrem Nachbarn liegt, hat keinen Winkel.
    if (!a || !b) return null;

    const bisectorLength = Math.hypot(a.x + b.x, a.y + b.y);

    // Gestreckt: Die Schenkel zeigen genau entgegengesetzt, es gibt keine Winkelhalbierende — und
    // auch keinen Winkel, den zu markieren sich lohnte.
    if (bisectorLength === 0) return null;

    const bisector = { x: (a.x + b.x) / bisectorLength, y: (a.y + b.y) / bisectorLength };

    return {
        from: { x: vertex.x + a.x * radius, y: vertex.y + a.y * radius },
        through: { x: vertex.x + bisector.x * radius, y: vertex.y + bisector.y * radius },
        to: { x: vertex.x + b.x * radius, y: vertex.y + b.y * radius },
        // Auf halbem Weg zum Bogen: klar innerhalb, klar von der Ecke abgesetzt.
        dot: { x: vertex.x + bisector.x * radius * 0.5, y: vertex.y + bisector.y * radius * 0.5 },
    };
};
